import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Checkbox, Form, FormItem, Input, NumberField, Select, TextArea, Divider } from "~/components/ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState, type FunctionComponent } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import type { CriticalityLevel, InventoryRequestData, RequestType } from "@noa/api-core";
import type { ActionFormProps } from "../registry";

const REQUEST_TYPE_OPTIONS = [
  { value: "creation", label: "Création d'article" },
  { value: "inventory_critical", label: "Mise en inventaire - Pièce critique" },
  { value: "inventory_consumable", label: "Mise en inventaire - Consommable" },
];

const CONSEQUENCE_OPTIONS = [
  { value: 1, label: "1 - Aucun arrêt" },
  { value: 2, label: "2 - Contingence" },
  { value: 3, label: "3 - Arrêt partiel" },
  { value: 4, label: "4 - Arrêt complet" },
];

const PREDICTABILITY_OPTIONS = [
  { value: 1, label: "1 - Signalé par machine" },
  { value: 2, label: "2 - Inspection" },
  { value: 3, label: "3 - Difficile" },
  { value: 4, label: "4 - Indétectable" },
];

const FINANCIAL_RISK_OPTIONS = [
  { value: 1, label: "1 - Aucune perte" },
  { value: 2, label: "2 - < 25 k$" },
  { value: 3, label: "3 - 25-100 k$" },
  { value: 4, label: "4 - > 100 k$" },
];

const SUBSTITUTE_OPTIONS = [
  { value: 1, label: "1 - Disponible" },
  { value: 2, label: "2 - Modification" },
  { value: 3, label: "3 - Délai long" },
  { value: 4, label: "4 - Aucun" },
];

const criticalityLevelSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);

const inventoryRequestSchema = z.object({
  // Required fields
  requestType: z.enum(["creation", "inventory_critical", "inventory_consumable"]),
  descriptionFr: z.string().min(1, "La description est requise"),
  dangerousProduct: z.boolean(),
  userComments: z.string().min(1, "La justification est requise"),
  minRequested: z.number().min(0, "Requis"),
  maxRequested: z.number().min(1, "Requis"),

  // Optional product fields
  descriptionEn: z.string().optional(),
  detailedDescription: z.string().optional(),
  productGroup: z.string().optional(),
  manufacturer: z.string().optional(),
  modelNumber: z.string().optional(),
  catalogNumber: z.string().optional(),
  suggestedSupplier: z.string().optional(),
  internetLink: z.string().optional(),
  priceCAD: z.number().optional(),
  deliveryTime: z.string().optional(),

  // Optional inventory fields
  assetNumber: z.string().optional(),
  quantityPerAsset: z.number().optional(),
  annualUsageForecast: z.number().optional(),

  // Optional boolean fields
  repairable: z.boolean().optional(),
  simdut: z.boolean().optional(),

  // Optional criticality fields
  operationalConsequence: criticalityLevelSchema.optional(),
  breakPredictability: criticalityLevelSchema.optional(),
  financialRisk: criticalityLevelSchema.optional(),
  substituteAvailability: criticalityLevelSchema.optional(),
});

type InventoryRequestFormData = z.infer<typeof inventoryRequestSchema>;

export const InventoryRequestForm: FunctionComponent<ActionFormProps<InventoryRequestData>> = ({
  data,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [showOptional, setShowOptional] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InventoryRequestFormData>({
    resolver: zodResolver(inventoryRequestSchema),
    defaultValues: data as InventoryRequestFormData,
  });

  // Watch all form values and sync to parent
  const formValues = useWatch({ control });

  useEffect(() => {
    onChange(formValues as InventoryRequestData);
  }, [formValues, onChange]);

  const handleFormSubmit = () => {
    onSubmit();
  };

  return (
    <Form size="small" className="action-card-form-content" onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Required fields - compact layout */}
      <div className="action-card-form-grid">
        <FormItem
          label="Type"
          validateStatus={errors.requestType ? "error" : undefined}
          help={errors.requestType?.message}
          className="action-card-form-field"
          required
        >
          <Controller
            name="requestType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={REQUEST_TYPE_OPTIONS}
                placeholder="Sélectionner..."
                onChange={(value) => field.onChange(value as RequestType)}
              />
            )}
          />
        </FormItem>

        <FormItem
          label="Produit dangereux"
          validateStatus={errors.dangerousProduct ? "error" : undefined}
          help={errors.dangerousProduct?.message}
          className="action-card-form-field"
          required
        >
          <Controller
            name="dangerousProduct"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={[
                  { value: true, label: "Oui" },
                  { value: false, label: "Non" },
                ]}
                placeholder="Sélectionner..."
              />
            )}
          />
        </FormItem>
      </div>

      <FormItem
        label="Description"
        validateStatus={errors.descriptionFr ? "error" : undefined}
        help={errors.descriptionFr?.message}
        required
      >
        <Controller
          name="descriptionFr"
          control={control}
          render={({ field }) => <TextArea {...field} rows={2} placeholder="Description de l'article demandé..." />}
        />
      </FormItem>

      <div className="action-card-form-grid">
        <FormItem
          label="Min"
          validateStatus={errors.minRequested ? "error" : undefined}
          help={errors.minRequested?.message}
          className="action-card-form-field"
          required
        >
          <Controller
            name="minRequested"
            control={control}
            render={({ field }) => (
              <NumberField
                {...field}
                min={0}
                placeholder="0"
                className="w-full"
                onChange={(v) => field.onChange(v ?? 0)}
              />
            )}
          />
        </FormItem>
        <FormItem
          label="Max"
          validateStatus={errors.maxRequested ? "error" : undefined}
          help={errors.maxRequested?.message}
          className="action-card-form-field"
          required
        >
          <Controller
            name="maxRequested"
            control={control}
            render={({ field }) => (
              <NumberField
                {...field}
                min={1}
                placeholder="1"
                className="w-full"
                onChange={(v) => field.onChange(v ?? 1)}
              />
            )}
          />
        </FormItem>
      </div>

      <FormItem
        label="Justification"
        className="mb-3"
        validateStatus={errors.userComments ? "error" : undefined}
        help={errors.userComments?.message}
        required
      >
        <Controller
          name="userComments"
          control={control}
          render={({ field }) => <TextArea {...field} rows={2} placeholder="Contexte de la demande..." />}
        />
      </FormItem>

      {/* Optional fields toggle */}
      <button type="button" className="action-card-optional-toggle" onClick={() => setShowOptional(!showOptional)}>
        {showOptional ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        <span>Champs optionnels</span>
      </button>

      {showOptional && (
        <div className="action-card-optional-fields">
          <Divider className="my-2" />

          {/* Product details */}
          <div className="action-card-form-grid">
            <FormItem label="Fabricant" className="action-card-form-field">
              <Controller
                name="manufacturer"
                control={control}
                render={({ field }) => <Input {...field} placeholder="Ex: SKF" />}
              />
            </FormItem>
            <FormItem label="Modèle" className="action-card-form-field">
              <Controller
                name="modelNumber"
                control={control}
                render={({ field }) => <Input {...field} placeholder="Ex: 6205-2RS" />}
              />
            </FormItem>
          </div>

          <div className="action-card-form-grid">
            <FormItem label="Fournisseur" className="action-card-form-field">
              <Controller
                name="suggestedSupplier"
                control={control}
                render={({ field }) => <Input {...field} placeholder="Ex: WAJAX" />}
              />
            </FormItem>
            <FormItem label="Prix $CAD" className="action-card-form-field">
              <Controller
                name="priceCAD"
                control={control}
                render={({ field }) => (
                  <NumberField
                    {...field}
                    min={0}
                    placeholder="0.00"
                    className="w-full"
                    onChange={(v) => field.onChange(v ?? undefined)}
                  />
                )}
              />
            </FormItem>
          </div>

          <FormItem label="Actif Maximo" className="mb-3">
            <Controller
              name="assetNumber"
              control={control}
              render={({ field }) => <Input {...field} placeholder="Ex: A115936" />}
            />
          </FormItem>

          <FormItem label="Lien" className="mb-3">
            <Controller
              name="internetLink"
              control={control}
              render={({ field }) => <Input {...field} placeholder="https://..." />}
            />
          </FormItem>

          {/* Criticality assessment */}
          <div className="action-card-form-section-title">Criticité</div>

          <div className="action-card-form-grid">
            <FormItem label="Conséquence" className="action-card-form-field">
              <Controller
                name="operationalConsequence"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={CONSEQUENCE_OPTIONS}
                    placeholder="—"
                    allowClear
                    onChange={(v) => field.onChange(v as CriticalityLevel | undefined)}
                  />
                )}
              />
            </FormItem>
            <FormItem label="Prévisibilité" className="action-card-form-field">
              <Controller
                name="breakPredictability"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={PREDICTABILITY_OPTIONS}
                    placeholder="—"
                    allowClear
                    onChange={(v) => field.onChange(v as CriticalityLevel | undefined)}
                  />
                )}
              />
            </FormItem>
          </div>

          <div className="action-card-form-grid">
            <FormItem label="Risque $" className="action-card-form-field">
              <Controller
                name="financialRisk"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={FINANCIAL_RISK_OPTIONS}
                    placeholder="—"
                    allowClear
                    onChange={(v) => field.onChange(v as CriticalityLevel | undefined)}
                  />
                )}
              />
            </FormItem>
            <FormItem label="Substitut" className="action-card-form-field">
              <Controller
                name="substituteAvailability"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={SUBSTITUTE_OPTIONS}
                    placeholder="—"
                    allowClear
                    onChange={(v) => field.onChange(v as CriticalityLevel | undefined)}
                  />
                )}
              />
            </FormItem>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2">
            <FormItem className="mb-0">
              <Controller
                name="repairable"
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={field.onChange}>
                    Réparable
                  </Checkbox>
                )}
              />
            </FormItem>
            <FormItem className="mb-0">
              <Controller
                name="simdut"
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={field.onChange}>
                    SIMDUT
                  </Checkbox>
                )}
              />
            </FormItem>
          </div>
        </div>
      )}

      <FormItem className="mb-0 mt-3">
        <div className="inline-flex items-center gap-2">
          <Button variant="primary" type="submit" loading={isSubmitting}>
            Envoyer
          </Button>
          <Button onClick={onCancel} disabled={isSubmitting}>
            Annuler
          </Button>
        </div>
      </FormItem>
    </Form>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- prototype form + mock submit handler co-located per action-cards design spec
export async function submitInventoryRequest(data: InventoryRequestData): Promise<void> {
  console.log("[ACTION] Inventory request submitted:", data);
  await new Promise((resolve) => setTimeout(resolve, 1000));
}
